import clsx from 'clsx';
import React from 'react';
import { formatPhone } from '../../shared/phone';
import {
  Button,
  Callout,
  EmptyState,
  IconCalendar,
  IconSearch,
  Input,
  ListPage,
  Spinner,
  StatusPill,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../ui/v2';
import { formatGuestName, reservationOrigin } from '../Tables/reservationModel';
import {
  adminReservationsPageLabel,
  adminReservationStatusTone,
  formatAdminReservationDateTime,
  formatAdminReservationDeposit,
} from './adminReservationsModel';
import type { AdminReservationSortKey } from './platformAdminOrdering';
import type { AdminReservationsList } from './useAdminReservationsList';

const PHONE_SEARCH_ID = 'admin-reservations-phone-search';

const COLUMNS: {
  key: string;
  label: string;
  width?: number;
  sortKey?: AdminReservationSortKey;
}[] = [
  { key: 'store_name', label: 'Store Name', width: 180, sortKey: 'storeName' },
  { key: 'store_id', label: 'Store ID', width: 88, sortKey: 'storeId' },
  {
    key: 'updated_at',
    label: 'Last Updated At',
    width: 152,
    sortKey: 'updatedAt',
  },
  {
    key: 'start_time',
    label: 'Reservation Start Time',
    width: 184,
    sortKey: 'startTime',
  },
  { key: 'status', label: 'Status', width: 144, sortKey: 'status' },
  { key: 'source', label: 'Source', width: 96 },
  {
    key: 'confirmation_code',
    label: 'Confirmation Code',
    width: 152,
    sortKey: 'confirmationCode',
  },
  {
    key: 'reservation_id',
    label: 'Reservation ID',
    width: 160,
    sortKey: 'reservationId',
  },
  {
    key: 'customer_id',
    label: 'Customer ID',
    width: 112,
    sortKey: 'customerId',
  },
  { key: 'party_size', label: 'Party Size', width: 96, sortKey: 'partySize' },
  { key: 'guest_name', label: 'Guest Name', width: 160, sortKey: 'guestName' },
  {
    key: 'guest_phone',
    label: 'Guest Phone',
    width: 140,
    sortKey: 'guestPhone',
  },
  {
    key: 'guest_email',
    label: 'Guest Email',
    width: 200,
    sortKey: 'guestEmail',
  },
  {
    key: 'special_request',
    label: 'Special Request',
    width: 220,
    sortKey: 'specialRequest',
  },
  { key: 'deposit', label: 'Deposit', width: 96, sortKey: 'deposit' },
  { key: 'table_type', label: 'Table Type', width: 120, sortKey: 'tableType' },
];

const MissingValue: React.FC = () => <span className="text-v2-fg-4">—</span>;

const AdminReservationsTab: React.FC<{ list: AdminReservationsList }> = ({
  list,
}) => {
  const {
    reservations,
    loading,
    page,
    totalPages,
    count,
    phoneSearch,
    debouncedPhoneSearch,
    listError,
    sort,
    onPhoneSearchChange,
    onSort,
    onPreviousPage,
    onNextPage,
  } = list;
  const showFirstLoad = loading && reservations.length === 0;
  const isRefreshing = loading && reservations.length > 0;

  let body: React.ReactNode;
  if (!showFirstLoad && reservations.length === 0) {
    body = debouncedPhoneSearch ? (
      <EmptyState
        icon={<IconSearch />}
        title="You don't have any matching reservations at the moment"
        description="When matching reservations appear, they'll show up here."
      />
    ) : (
      <EmptyState
        icon={<IconCalendar />}
        title="You don't have any reservations at the moment"
        description="When reservations appear, they'll show up here."
      />
    );
  } else {
    body = (
      <>
        {showFirstLoad && (
          <p role="status" className="sr-only">
            Loading...
          </p>
        )}
        <div
          className={clsx(
            'v2-scroll overflow-x-auto transition-opacity',
            isRefreshing && 'opacity-70',
          )}
        >
          <Table fixed className="min-w-[1600px]">
            <THead>
              <tr>
                {COLUMNS.map((column) => (
                  <TH
                    key={column.key}
                    width={column.width}
                    sortable={Boolean(column.sortKey) && !showFirstLoad}
                    active={
                      Boolean(column.sortKey) && sort.key === column.sortKey
                    }
                    dir={sort.direction}
                    onSort={() => column.sortKey && onSort(column.sortKey)}
                  >
                    {column.label}
                  </TH>
                ))}
              </tr>
            </THead>
            <TBody>
              {showFirstLoad ? (
                <TableSkeletonRows cols={COLUMNS.length} />
              ) : (
                reservations.map((r, idx) => {
                  const guestName = formatGuestName(r);
                  const origin = reservationOrigin(r.origin_channel);
                  const deposit = formatAdminReservationDeposit(r.deposit);
                  return (
                    <TR key={`${r.reservation_id || r.id}-${idx}`}>
                      <TD truncate className="font-medium">
                        {r.store_name || <MissingValue />}
                      </TD>
                      <TD num className="text-v2-fg-2">
                        {r.store_id || <MissingValue />}
                      </TD>
                      <TD num className="whitespace-nowrap">
                        {formatAdminReservationDateTime(r.updated_at)}
                      </TD>
                      <TD num className="whitespace-nowrap">
                        {formatAdminReservationDateTime(r.start_time)}
                      </TD>
                      <TD>
                        {r.status ? (
                          <StatusPill
                            tone={adminReservationStatusTone(r.status)}
                          >
                            {r.status.toUpperCase()}
                          </StatusPill>
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD>
                        {origin ? (
                          <span title={origin.title}>
                            <Tag tone={origin.tone}>{origin.label}</Tag>
                          </span>
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD>
                        {r.confirmation_code ? (
                          <Tag className="font-mono">{r.confirmation_code}</Tag>
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD
                        truncate
                        className="font-mono text-v2-12 text-v2-fg-2"
                      >
                        <span
                          title={
                            r.reservation_id
                              ? String(r.reservation_id)
                              : undefined
                          }
                        >
                          {r.reservation_id || <MissingValue />}
                        </span>
                      </TD>
                      <TD num className="text-v2-fg-2">
                        {r.customer_id || <MissingValue />}
                      </TD>
                      <TD num>{r.party_size ?? <MissingValue />}</TD>
                      <TD truncate className="font-medium">
                        {guestName || <MissingValue />}
                      </TD>
                      <TD num className="whitespace-nowrap text-v2-fg-2">
                        {r.guest_phone_number ? (
                          formatPhone(r.guest_phone_number)
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD truncate>
                        {r.guest_email ? (
                          <a
                            href={`mailto:${r.guest_email}`}
                            className="text-v2-accent hover:underline"
                          >
                            {r.guest_email}
                          </a>
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD>
                        <span
                          title={r.special_request || undefined}
                          className="line-clamp-2 text-v2-fg-2"
                        >
                          {r.special_request || <MissingValue />}
                        </span>
                      </TD>
                      <TD num>{deposit || <MissingValue />}</TD>
                      <TD>
                        {r.table_type ? (
                          <Tag>{r.table_type}</Tag>
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        </div>
      </>
    );
  }

  return (
    <ListPage
      title="OpenTable Reservations (Admin)"
      actions={
        isRefreshing ? (
          <span
            role="status"
            className="inline-flex items-center gap-1.5 text-v2-12 text-v2-fg-3"
          >
            <Spinner />
            Updating reservations...
          </span>
        ) : null
      }
      toolbar={
        <div className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 sm:flex-nowrap sm:py-0 lg:px-6">
          <label
            htmlFor={PHONE_SEARCH_ID}
            className="shrink-0 text-v2-12 font-medium text-v2-fg-3"
          >
            Search by phone
          </label>
          <div className="w-full min-w-0 sm:w-[280px] sm:shrink">
            <Input
              id={PHONE_SEARCH_ID}
              type="search"
              inputMode="tel"
              size="sm"
              placeholder="Search phone number..."
              value={phoneSearch}
              onChange={onPhoneSearchChange}
              leading={<IconSearch size={14} />}
            />
          </div>
        </div>
      }
    >
      {listError && (
        <div className="border-b border-v2-line px-4 py-3 lg:px-6">
          <Callout tone="danger">{listError}</Callout>
        </div>
      )}

      {body}

      <div className="flex h-10 items-center justify-center gap-2 border-t border-v2-line px-4 text-v2-12 text-v2-fg-3 lg:px-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPreviousPage}
          disabled={page <= 1}
        >
          Previous
        </Button>
        <span className="v2-num">
          {adminReservationsPageLabel(page, totalPages, count)}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onNextPage}
          disabled={page >= totalPages}
        >
          Next
        </Button>
      </div>
    </ListPage>
  );
};

export default AdminReservationsTab;
